// permissions.ts — Centralized Role-Based Access Control
//
// Single source of truth for all role definitions and permission checks.
// Import from here everywhere — never hardcode role strings in components.
//
// Role hierarchy (ascending privilege):
//   USER → BETA_TESTER → SUPPORT → MODERATOR → DEVELOPER → OWNER

export const USER_ROLES = [
  'USER',
  'BETA_TESTER',
  'SUPPORT',
  'MODERATOR',
  'DEVELOPER',
  'OWNER',
] as const;

export type UserRole = typeof USER_ROLES[number];

/** Numeric rank for each role — higher = more privileged. */
const ROLE_LEVEL: Record<UserRole, number> = {
  USER:        0,
  BETA_TESTER: 1,
  SUPPORT:     2,
  MODERATOR:   3,
  DEVELOPER:   4,
  OWNER:       5,
};

/**
 * Returns true if `userRole` meets or exceeds `required`.
 * Unknown / null role strings are treated as USER (rank 0 — fail-closed).
 */
export function hasRequiredRole(
  userRole: string | null | undefined,
  required: UserRole,
): boolean {
  const level = ROLE_LEVEL[userRole as UserRole] ?? 0;
  return level >= ROLE_LEVEL[required];
}

// ─── Convenience predicates ───────────────────────────────────────────────────
export const isUser        = (r: string | null | undefined) => hasRequiredRole(r, 'USER');
export const isBetaTester  = (r: string | null | undefined) => hasRequiredRole(r, 'BETA_TESTER');
export const isSupport     = (r: string | null | undefined) => hasRequiredRole(r, 'SUPPORT');
export const isModerator   = (r: string | null | undefined) => hasRequiredRole(r, 'MODERATOR');
export const isDeveloper   = (r: string | null | undefined) => hasRequiredRole(r, 'DEVELOPER');
export const isOwner       = (r: string | null | undefined) => hasRequiredRole(r, 'OWNER');

// ─── Named permission gates ───────────────────────────────────────────────────
export const PERMISSIONS = {
  CAN_ACCESS_BETA_LAB:        (r: string | null | undefined) => isBetaTester(r),
  CAN_ACCESS_MISSION_CONTROL: (r: string | null | undefined) => isOwner(r),
  CAN_RUN_PIPELINE:           (r: string | null | undefined) => isOwner(r),
  CAN_RESET_SYSTEM:           (r: string | null | undefined) => isOwner(r),
  CAN_ACCESS_DEV_TOOLS:       (r: string | null | undefined) => isOwner(r),
  CAN_VIEW_DIAGNOSTICS:       (r: string | null | undefined) => isBetaTester(r),
  CAN_USE_APP:                (r: string | null | undefined) => isUser(r),
} as const;

// ─── Mission Control session unlock ──────────────────────────────────────────
// After the owner completes the hidden tap sequence + PIN, this key is set
// so the sidebar shows the Mission Control link.
// The SERVER always re-validates the role on every request — this is UI-only.

export const MC_UNLOCK_KEY = 'wr-mission-control-unlocked';

export function isMCUnlocked(): boolean {
  try { return sessionStorage.getItem(MC_UNLOCK_KEY) === '1'; } catch { return false; }
}
export function unlockMissionControl(): void {
  try { sessionStorage.setItem(MC_UNLOCK_KEY, '1'); } catch { /* ignore */ }
}
export function lockMissionControl(): void {
  try { sessionStorage.removeItem(MC_UNLOCK_KEY); } catch { /* ignore */ }
}
